import { Subject } from 'rxjs';
import { appConfig } from '../../request/app.config';
import { NetworkRequestOptions } from '../../request/interfaces/request-options';
import { courierService } from '../../request/services/courier.service';
import { userService } from '../services/user.service';
import { SESSION_KEY } from '../../request/constants/constants';
import { localStorageService } from '../../storage/local-storage.service';
import { LoginCredentials, ResetPasswordRequestType } from '../interfaces/auth';
import { RecaptchaTokenPayload } from '../interfaces/recaptcha';

export const AuthAPI = {
  authURL: `${appConfig.getSecurityServiceURL()}/auth`,
  oAuthURL: `${appConfig.getSecurityServiceURL()}/oauth`,
  tfaAuthURL: `${appConfig.getSecurityServiceURL()}/tfa`,
  samlURL: `${appConfig.getSecurityServiceURL()}/saml`,
  multiRegionURL: `${appConfig.getSecurityServiceURL()}/multi-region`,
  userDetailsURL: `${appConfig.getSecurityServiceURL()}/users/me`,
  usersURL: `${appConfig.getSecurityServiceURL()}/users`,

  logoutSubject: new Subject<void>(),
  beforeLogoutSubject: new Subject<void>(),

  getLoggedInUser(updateThirdPartyData = true) {
    return courierService
      .get(this.userDetailsURL, {
        uiOptions: {
          handleUnauthorisedResponse: false
        }
      })
      .then(
        res => {
          userService.setUser(res.data);

          if (updateThirdPartyData) {
            userService.updateThirdPartyData();
          }
        },
        err => {
          if (err.status === 404) {
            courierService.unauthorisedErrorObs.next(false);
          }

          throw err;
        }
      );
  },

  getAuthConfigForLogin(payload: { email: string } & RecaptchaTokenPayload) {
    const requestUrl = `${this.authURL}/login-auth-configurations`;

    const options: NetworkRequestOptions = {
      uiOptions: {
        handleUnauthorisedResponse: false
      }
    };

    return courierService.post(requestUrl, payload, options);
  },

  login(
    credentials: LoginCredentials & RecaptchaTokenPayload,
    options?: NetworkRequestOptions,
    tfaCode?: string
  ) {
    const body: LoginCredentials & RecaptchaTokenPayload & { otp?: string } = {
      ...credentials
    };

    if (tfaCode) {
      body.otp = tfaCode;
    }

    return courierService.post(`${this.authURL}/authenticate`, body, options);
  },

  loginWithTemporaryToken(temporaryToken: string) {
    const requestUrl = `${this.multiRegionURL}/session`;

    const options: NetworkRequestOptions = {
      networkOptions: {
        params: new URLSearchParams({
          temp_session_token: temporaryToken
        })
      }
    };

    return courierService.get(requestUrl, options);
  },

  loginWithCode(code: string) {
    const requestUrl = `${this.authURL}/authenticate/code/${code}`;

    const options: NetworkRequestOptions = {
      uiOptions: {
        handleUnauthorisedResponse: false
      }
    };

    return courierService.post(requestUrl, undefined, options);
  },

  googleLogin(error?: string, code?: string, state?: string, tfaCode?: string) {
    const options: NetworkRequestOptions = {
      uiOptions: {
        handleUnauthorisedResponse: false
      },
      networkOptions: {
        params: new URLSearchParams({
          ...(error ? { error } : {}),
          ...(code ? { code } : {}),
          ...(state ? { state } : {}),
          ...(tfaCode ? { otp: tfaCode } : {})
        })
      }
    };

    return courierService.get(`${this.oAuthURL}/google/callback`, options);
  },

  verifyEmail(code: string) {
    const options: NetworkRequestOptions = {
      uiOptions: {
        showErrorMsg: true,
        handleUnauthorisedResponse: false
      }
    };

    const params = {
      code
    };

    return courierService.put(`${this.authURL}/verify-email`, params, options);
  },

  authenticateAccount(email: string) {
    const requestUrl = `${this.authURL}/authenticate/hevo`;

    const params = {
      email
    };

    const options: NetworkRequestOptions = {
      uiOptions: {
        handleUnauthorisedResponse: false
      }
    };

    return courierService.post(requestUrl, params, options);
  },

  isUserSessionStillValid() {
    const options: NetworkRequestOptions = {
      uiOptions: {
        handleUnauthorisedResponse: false
      }
    };

    return courierService.get(this.userDetailsURL, options).then(
      () => {
        return true;
      },
      err => {
        return !(err.status === 404 || err.status === 401);
      }
    );
  },

  forgotPassword(params: { email: string }) {
    const requestUrl = `${this.authURL}/forgot-password`;

    return courierService.put(requestUrl, params);
  },

  getSAMLRequestURL(email: string) {
    const requestUrl = `${this.samlURL}/authn-request`;

    const options: NetworkRequestOptions = {
      networkOptions: {
        params: new URLSearchParams({
          email
        })
      }
    };

    return courierService.get(requestUrl, options);
  },

  resetPassword(code: string, password: string, type: ResetPasswordRequestType) {
    const requestUrl = `${this.authURL}/reset-password`;

    const params = {
      code,
      password,
      type
    };

    const options: NetworkRequestOptions = {
      uiOptions: {
        handleUnauthorisedResponse: false
      }
    };

    return courierService.put(requestUrl, params, options);
  },

  submitOTP(otp: number) {
    const requestUrl = `${this.authURL}/authenticate/otp`;

    const params = {
      otp
    };

    const options: NetworkRequestOptions = {
      uiOptions: {
        handleUnauthorisedResponse: false
      }
    };

    return courierService.post(requestUrl, params, options);
  },

  getTfaRecoveryCodes() {
    const requestUrl = `${this.tfaAuthURL}/recovery-codes`;

    return courierService.get(requestUrl);
  },

  validateTfaToken(
    params: { otp: number; secret: string },
    handleUnauthorisedResponse = true,
    showErrorMsg = true
  ) {
    const requestUrl = `${this.tfaAuthURL}/setup`;

    const options: NetworkRequestOptions = {
      uiOptions: {
        showErrorMsg,
        handleUnauthorisedResponse
      }
    };

    return courierService.post(requestUrl, params, options);
  },

  getTfaQR(handleUnauthorisedResponse = true) {
    const requestUrl = `${this.tfaAuthURL}/qr`;

    const options: NetworkRequestOptions = {
      uiOptions: {
        handleUnauthorisedResponse
      }
    };

    return courierService.get(requestUrl, options);
  },

  getGoogleOAuthURL(searchParams: URLSearchParams) {
    return `${this.oAuthURL}/google/authenticate?${searchParams.toString()}`;
  },

  userAdditionalDetails(params: { user_role: string }) {
    const requestUrl = `${this.authURL}/user-additional-details`;

    const options: NetworkRequestOptions = {
      uiOptions: {
        handleUnauthorisedResponse: false
      }
    };

    return courierService.post(requestUrl, params, options);
  },

  logout() {
    const requestUrl = `${appConfig.getSecurityServiceURL()}/logout`;
    const options: NetworkRequestOptions = {
      uiOptions: {
        showErrorMsg: !!localStorageService.get(SESSION_KEY)
      }
    };

    this.beforeLogoutSubject.next();

    return courierService.delete(requestUrl, options);
  },

  getMembers() {
    return courierService.get(`${this.usersURL}/all-users`);
  }
};
